import React from 'react';
import {
  Modal,
  Table,
  Typography,
  Space,
  Divider,
  Tag,
} from 'antd';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { formatPrice } from '../../../lib/utils';

const { Title, Text } = Typography;

const FacturaDetailsModal = ({ visible, factura, onClose }) => {
  const signoMoneda = useSelector((state) => state.signoMoneda.signo_moneda);
  
  if (!factura) return null;
  
  // Calculate totals
  const calculateProductTotal = (product) => {

    const subtotal = (product.cantidad || 0) * (product.precio_unitario || 0);
    const impuesto = product.producto?.grupo?.impuesto || 0;
    const impuestoValue = subtotal * (impuesto / 100);
    return {
      subtotal,
      impuestoValue,
      total: subtotal + impuestoValue,
      impuestoPorcentaje: impuesto
    };
  };

  const productTotals = factura.productos?.map(product => ({
    ...product,
    ...calculateProductTotal(product)
  })) || [];

  const totalSubtotal = productTotals.reduce((sum, product) => sum + product.subtotal, 0);
  const totalImpuesto = productTotals.reduce((sum, product) => sum + product.impuestoValue, 0);
  const totalGeneral = productTotals.reduce((sum, product) => sum + product.total, 0);
  
  // Define columns for products table
  const productColumns = [
    {
      title: 'Producto',
      dataIndex: 'producto',
      key: 'producto',
      render: (producto) => producto?.nombre || 'N/A',
    },
    {
      title: 'Cantidad',
      dataIndex: 'cantidad',
      key: 'cantidad',
      align: 'center',
    },
    {
      title: 'Precio Unitario',
      dataIndex: 'precio_unitario',
      key: 'precio_unitario',
      align: 'right',
      render: (value) => formatPrice(value,signoMoneda),
    },
    {
      title: 'Subtotal',
      key: 'subtotal',
      align: 'right',
      render: (_, record) => formatPrice(record.subtotal, signoMoneda),
    },
    {
      title: 'Impuesto',
      key: 'impuesto',
      align: 'center',
      render: (_, record) => (
        <div>
          <div>{record.impuestoPorcentaje}%</div>
        
        </div>
      ),
    },
    {
      title: 'Total',
      key: 'total',
      align: 'right',
      render: (_, record) => formatPrice(record.total,signoMoneda),
    },
  ];

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'creada':
        return 'green';
      case 'devolucion':
        return 'red';
      default:
        return 'default';
    }
  };

  const getTipoText = (tipo) => {
    switch (tipo) {
      case 1:
        return 'Venta';
      case 2:
        return 'Crédito';
      default:
        return 'N/A';
    }
  };

  return (
    <Modal
      title={
        <Space direction="vertical" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            Detalles de Factura
          </Title>
          <Text type="secondary">
            Consecutivo: {factura.consecutivo}
          </Text>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        {/* Factura Info */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space direction="vertical">
            <Text strong>Fecha: {moment(factura.date_created).format('DD/MM/YYYY HH:mm')}</Text>
            <Text>Caja: {factura.caja?.id || 'N/A'}</Text>
          </Space>
          <Space direction="vertical" align="end">
            <Tag color={getEstadoColor(factura.estado)}>
              {factura.estado?.toUpperCase()}
            </Tag>
           
          </Space>
        </div>

        <Divider />

        {/* Products Table */}
        <div>
          <Title level={5}>Productos</Title>
          <Table
            columns={productColumns}
            dataSource={productTotals}
            rowKey={(record, index) => index}
            pagination={false}
            size="small"
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={3}>
                  <Text strong>Total General</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Text strong>{formatPrice(totalSubtotal,signoMoneda)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="center">
                  <Text strong>{formatPrice(totalImpuesto,signoMoneda)}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} align="right">
                  <Text strong>{formatPrice(totalGeneral,signoMoneda)}</Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
        </div>

        {/* Summary */}
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '6px',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Space direction="vertical">
            <Text>Subtotal: {formatPrice(totalSubtotal,signoMoneda)}</Text>
            <Text>Impuestos: {formatPrice(totalImpuesto,signoMoneda)}</Text>
          </Space>
          <div style={{ textAlign: 'right' }}>
            <Title level={3} style={{ margin: 0, color: '#1890ff' }}>
              Total: {formatPrice(totalGeneral,signoMoneda)}
            </Title>
          </div>
        </div>
      </Space>
    </Modal>
  );
};

export default FacturaDetailsModal; 